import _ from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { traverseTree, traverseTrees } from '@/utils/tree-kit';
import {
    GroupRoleType,
    IBackgroundLayerProps,
    IBrushLayerProps,
    IChartLayerProps,
    IImageLayerProps,
    IImageTransform,
    ILayerProps,
    IMainLayerProps,
    IMarkupLayerProps,
    IMediaLayerProps,
    IQrCodeLayerProps,
    ITableLayerProps,
    IVectorLayerProps,
    IWatermarkLayerProps,
    LayerType,
} from '@/version-types';
import { ITemplateConfig } from '../server-template-types/index';
import {
    checkServerTableLayerPropsIsVersion15_0_0,
    IMagnifierLayerProps,
    IServerBgLayerProps,
    IServerBrushLayerProps,
    IServerChartLayerProps,
    IServerGroupLayerProps,
    IServerImageLayerProps,
    IServerLayerProps,
    IServerMainLayerProps,
    IServerMarkupLayerProps,
    IServerMediaLayerProps,
    IServerPuzzleLayerProps,
    IServerQrCodeLayerProps,
    IServerTableLayerProps,
    IServerTextLayerProps,
    IServerVectorLayerProps,
    IServerWatermarkLayerProps,
    parseTableLayerProps,
} from '../server-template-types/layer';
import { VERSION } from '../versions';
import { parseLayersToLatestVersion } from './parse-layers-to-latest-version';

export const parseServerToClientMap = {
    [LayerType.BACKGROUND_LAYER]: (layer: IServerLayerProps) => {
        const {
            url,
            cutoutMaskURL,
            maskURL,
            stretchOption,
            backgroundColor,
            prompt,
            isAILayer,
            imageTransform = {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            },
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            naturalWidth,
            naturalHeight,
            lock,
            hidden,
            flipH,
            flipV,
            maskType,
            parentId,
            title,
            materialIds,
            backgroundGradient,

            fitWidth,

            fitHeight,
            extraInfo,
            dynamicInfo,
        } = layer as IServerBgLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,
            isAILayer,
            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            prompt,

            /** 图层数据 */
            data: {
                /** 元素url */
                url,
                /** 遮罩元素 */
                maskURL,

                /** 图片拉伸模式 */
                stretchOption,

                /** 原始图片宽度 */
                naturalWidth,

                /** 原始图片高度 */
                naturalHeight,

                /** 图片裁剪 */
                imageTransform: formatImageTransformToClient(imageTransform, width, height),
                /** 蒙版类型 */
                maskType,
                /** 抠图 */
                cutoutMaskURL,

                backgroundColor,

                backgroundGradient,
                dynamicInfo,
            } as IBackgroundLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.GROUP_LAYER]: (layer: IServerLayerProps): ILayerProps => {
        const {
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,
            mode,
            category,
            parentId,
            title,
            materialIds,
            children,
            isTextMaterial,
            isAILayer,
            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer as IServerGroupLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,
            isAILayer,
            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            /** 图层数据 */
            data: {
                isExtraGroup: false,
                isTextMaterial,
            },

            prompt,
            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children:
                (children?.map((layer: IServerLayerProps) => {
                    if (parseServerToClientMap[layer.layerType]) {
                        return parseServerToClientMap[layer.layerType](layer);
                    }
                    return layer;
                }) as ILayerProps[]) || [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 图片功能 */
            mode,
            /** 标签 */
            category,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.IMAGE_LAYER]: (layer: IServerLayerProps) => {
        const {
            url,
            cutoutMaskURL,
            maskURL,
            stretchOption,
            imageTransform = {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            },
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            naturalWidth,
            naturalHeight,
            isAILayer,
            lock,
            hidden,
            flipH,
            flipV,
            maskType,
            mode,
            category,
            parentId,
            title,
            materialIds,
            effect,
            filter,
            prompt,
            hint,
            aiTextExtension,
            fitWidth,
            fitHeight,
            minimumMask,
            maskFeathering,
            blendMode,
            extraInfo,
            borderRadius,
            mcpEffects,
            fill,
        } = layer as IServerImageLayerProps;

        const data = {
            /** 元素url */ url,
            /** 遮罩元素 */
            maskURL,

            /** 图片拉伸模式 */
            stretchOption,

            /** 原始图片宽度 */
            naturalWidth,

            /** 原始图片高度 */
            naturalHeight,

            /** 图片裁剪 */
            imageTransform: formatImageTransformToClient(imageTransform, width, height),
            /** 蒙版类型 */
            maskType,
            /** 抠图 */
            cutoutMaskURL,
            effect,
            filter,
            hint,
            aiTextExtension,
            minimumMask,
            maskFeathering,
            blendMode,
            borderRadius,
            mcpEffects,
            fill,
        } as IImageLayerProps;

        if (!data.fill || !data.fill.color) delete data.fill;

        const newLayer = {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            // 透明度
            opacity,

            prompt,
            isAILayer,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 图片功能 */
            mode,
            /** 标签 */
            category,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;

        return newLayer;
    },
    [LayerType.TEXT_LAYER]: (layer: IServerLayerProps) => {
        const {
            text,
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            lock,
            shine,
            isAILayer,

            color,
            fontSize,
            fontFamily,
            lineSpacing,
            letterSpacing,
            bold,
            italic,
            lineThrough,
            underline,
            textAlign,
            writingMode,
            textGlow,
            textShadow,
            textStroke,
            hidden,
            parentId,
            title,
            materialIds,
            textBackground,
            textGradient,
            listStyle,
            flipH,
            flipV,
            texture,
            textMark,

            prompt,
            textContents,
            shapeExtension,
            screenshotUrl,

            fitWidth,

            fitHeight,
            extraInfo,
            advancedTextMaterial,
            listStyleMaterial,
        } = layer as IServerTextLayerProps;

        const result = {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            isAILayer,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            lock: lock === undefined ? false : lock,

            shine,
            hidden,
            parentId,
            title,
            materialIds: materialIds || {},

            prompt,
            children: [],

            data: {
                text: text.replace(/(\r\n|\r)/g, '\n'),
                color,
                fontSize,
                fontFamily,
                lineSpacing,
                letterSpacing,
                bold,
                italic,
                lineThrough,
                underline,
                textAlign,
                writingMode,
                textGlow,
                textShadow,
                textStroke,
                textIndex: 0,
                textBackground,
                textGradient,
                listStyle: listStyle || '',
                textContents: textContents || [],
                shapeExtension,
                screenshotUrl,
                texture,
                textMark,
                advancedTextMaterial,
                listStyleMaterial,
            },

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;

        return result;
    },
    [LayerType.WATERMARK]: (layer: IServerLayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,
            isAILayer,
            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            /** 图片功能 */
            mode,
            /** 标签 */
            category,

            url,

            zipUrl,

            // 是否全屏水印
            isFullScreen,

            // 横向位移
            offsetX,

            // 纵向位移
            offsetY,

            // 水印大小
            size,

            // 间距
            space,

            // 交错
            cross,

            // 文字内容，有url时为空
            texts,

            // 文字颜色#FFFFFFFF，有url时为空
            textColor,

            // 字体，有url时为空
            fontFamily,

            children,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer as IServerWatermarkLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            isAILayer,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            children,

            /** 图层数据 */
            data: {
                // 是否全屏水印
                isFullScreen,

                // 横向位移
                offsetX,

                // 纵向位移
                offsetY,

                // 水印大小
                size,

                // 间距
                space,

                // 交错
                cross,

                // 文字内容，有url时为空
                texts,

                // 文字颜色#FFFFFFFF，有url时为空
                textColor,

                // 字体，有url时为空
                fontFamily,

                url,

                zipUrl,
            } as IWatermarkLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 图片功能 */
            mode,
            /** 标签 */
            category,

            // 父级id
            parentId,

            // 图层名称
            title,

            prompt,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as unknown as ILayerProps;
    },
    [LayerType.VECTOR_LAYER]: (layer: IServerLayerProps) => {
        const {
            url,
            imageTransform,
            stretchOption,
            naturalWidth,
            naturalHeight,
            vectorType,
            colors,
            fill,
            fillGradient,
            radius,
            stroke,
            strokeGradient,
            strokeWidth,
            strokeDasharray,
            strokeLinecap,
            effect,
            blur,
            strokeHidden,
            pickerIndex,

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,
            mode,
            category,
            parentId,
            title,
            materialIds,
            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
            borderRadius,
            textInfo,
        } = layer as IServerVectorLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            prompt,

            /** 图层数据 */
            data: {
                url,
                imageTransform,
                stretchOption,
                naturalWidth,
                naturalHeight,
                vectorType,
                colors,
                fill,
                fillGradient,
                radius,
                stroke,
                strokeGradient,
                strokeWidth,
                strokeDasharray,
                strokeLinecap,
                pickerIndex,
                effect,
                blur,
                strokeHidden,
                borderRadius,
                textInfo,
            } as IVectorLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 图片功能 */
            mode,
            /** 标签 */
            category,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.TABLE]: (layer: IServerLayerProps) => {
        const lastVersionLayer: IServerTableLayerProps = checkServerTableLayerPropsIsVersion15_0_0(layer)
            ? parseTableLayerProps(layer)
            : (layer as IServerTableLayerProps);

        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            lock,

            shine,

            screenshotUrl,
            allStyleId,
            cells,
            /** 预设相关的数据 */
            baseCells,
            styles,
            presetId,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,

            extraInfo,
        } = lastVersionLayer;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            // 透明度
            opacity,

            prompt,

            /** 图层数据 */
            data: {
                screenshotUrl,
                allStyleId,
                cells,
                baseCells,
                styles,
                presetId,
            } as ITableLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,

            extraInfo,
        } as ILayerProps;
    },
    [LayerType.QR_CODE]: (layer: IServerLayerProps) => {
        const {
            imageTransform,
            stretchOption,
            text,
            official,
            fill,
            stroke,
            strokeGradient,

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,
            parentId,
            title,
            materialIds,
            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer as IServerQrCodeLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            prompt,

            /** 图层数据 */
            data: {
                imageTransform,
                stretchOption,
                text,
                official,
                fill,
                stroke,
                strokeGradient,
            } as IQrCodeLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.BRUSH_LAYER]: (layer: IServerLayerProps) => {
        const {
            brushType,
            url,
            fills,
            stroke,
            startPoint,
            endPoint,
            radius,
            imageTransform,
            stretchOption,
            naturalWidth,
            naturalHeight,
            screenshotUrl,

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,

            parentId,
            title,
            materialIds,
            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer as IServerBrushLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            prompt,

            /** 图层数据 */
            data: {
                brushType,
                url,
                fills,
                stroke,
                startPoint,
                endPoint,
                radius,
                imageTransform,
                stretchOption,
                naturalWidth,
                naturalHeight,
                screenshotUrl,
            } as IBrushLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.MAIN_LAYER]: (layer: IServerLayerProps) => {
        const {
            url,
            cutoutMaskURL,
            maskURL,
            stretchOption,
            imageTransform = {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            },
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            naturalWidth,
            naturalHeight,
            isAILayer,
            lock,
            hidden,
            flipH,
            flipV,
            maskType,
            mode,
            category,
            parentId,
            title,
            materialIds,
            effect,
            filter,
            prompt,
            hint,
            isTransparent,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer as IServerMainLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            prompt,
            isAILayer,

            /** 图层数据 */
            data: {
                /** 元素url */
                url,
                /** 遮罩元素 */
                maskURL,

                /** 图片拉伸模式 */
                stretchOption,

                /** 原始图片宽度 */
                naturalWidth,

                /** 原始图片高度 */
                naturalHeight,

                /** 图片裁剪 */
                imageTransform: formatImageTransformToClient(imageTransform, width, height),
                /** 蒙版类型 */
                maskType,
                /** 抠图 */
                cutoutMaskURL,
                effect,
                filter,
                hint,
                isTransparent,
            } as IMainLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 图片功能 */
            mode,
            /** 标签 */
            category,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.MAGNIFIER]: (layer: IServerLayerProps) => {
        const {
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            isAILayer,
            lock,
            hidden,
            flipH,
            flipV,
            parentId,
            title,
            materialIds,

            fitWidth,

            fitHeight,
            extraInfo,
            prompt,
            url,

            size,

            hFlip,

            strokeColor,

            strokeSize,

            enableOffset,

            x,

            y,

            scale,

            enableStroke,
        } = layer as IMagnifierLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            prompt,
            isAILayer,

            /** 图层数据 */
            data: {
                url,

                size,

                hFlip,

                strokeColor,

                strokeSize,

                enableOffset,

                x,

                y,

                scale,

                enableStroke,
            } as IMagnifierLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.MARKUP_LAYER]: (layer: IServerLayerProps) => {
        const {
            markupType,
            label,
            anchor,
            centerOffset,
            hasLine,
            fill,
            startPoint,
            endPoint,
            imageTransform,
            screenshotUrl,

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,

            parentId,
            title,
            materialIds,
            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer as IServerMarkupLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            prompt,

            /** 图层数据 */
            data: {
                markupType,
                label,
                anchor,
                centerOffset,
                hasLine,
                fill,
                startPoint,
                endPoint,
                imageTransform,
                screenshotUrl,
            } as IMarkupLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.MEDIA_LAYER]: (layer: IServerLayerProps) => {
        const {
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            isAILayer,
            lock,
            hidden,
            flipH,
            flipV,
            parentId,
            title,
            materialIds,
            prompt,
            fitWidth,
            fitHeight,
            extraInfo,
            url,
            imageTransform,
            mediaType,
            naturalWidth,
            naturalHeight,
            autoplay,
            loop,
            controls,
            borderRadius,
            maskURL,
            poster,
        } = layer as IServerMediaLayerProps;

        return {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            /** 翻转 */
            ...(flipH && { flipH }),
            ...(flipV && { flipV }),

            // 透明度
            opacity,

            prompt,
            isAILayer,

            /** 图层数据 */
            data: {
                url,
                imageTransform,
                mediaType,
                naturalWidth,
                naturalHeight,
                autoplay,
                loop,
                controls,
                borderRadius,
                maskURL,
                poster,
            } as IMediaLayerProps,

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children: [],

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            // 父级id
            parentId,

            // 图层名称
            title,

            materialIds: materialIds || {},

            fitWidth,

            fitHeight,
            extraInfo,
        } as ILayerProps;
    },
    [LayerType.CHART]: (layer: IServerLayerProps) => {
        const {
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,
            parentId,
            title,
            materialIds,
            prompt,
            fitWidth,
            fitHeight,
            extraInfo,

            screenshotUrl,

            type,

            chartData,
            /** 颜色数组, 长度必须与图表的系列数一致 */
            colors,
            grid,
            legend,
            /** 类别坐标轴(对应chartData中的项目). 饼图时, 该值不生效. 但必须有值, 用于图表类型切换后的展示 */
            categoryAxisLabel,
            /** 值坐标轴, 同类别坐标轴 */
            valueAxisLabel,
            series,
        } = layer as IServerChartLayerProps;

        return {
            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,
            parentId,
            title,
            materialIds,
            prompt,
            fitWidth,
            fitHeight,
            extraInfo,
            children: [],
            data: {
                screenshotUrl,

                type,

                chartData,
                /** 颜色数组, 长度必须与图表的系列数一致 */
                colors,
                grid,
                legend,
                /** 类别坐标轴(对应chartData中的项目). 饼图时, 该值不生效. 但必须有值, 用于图表类型切换后的展示 */
                categoryAxisLabel,
                /** 值坐标轴, 同类别坐标轴 */
                valueAxisLabel,
                series,
            } as IChartLayerProps,
        };
    },
    [LayerType.PUZZLE]: (layer: IServerLayerProps) => {
        const {
            id,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,

            parentId,
            title,
            materialIds,

            isAILayer,
            prompt,

            fitWidth,

            fitHeight,
            extraInfo,

            edgeCornerRadius,
            edgeInnerWidth,
            edgeOuterWidth,
            puzzleMode,
            backgroundLayer,
            frameScale,
        } = layer as IServerPuzzleLayerProps;

        let { containerLayers } = layer as IServerPuzzleLayerProps;

        const children = [] as ILayerProps[];

        return {
            id,
            layerType: LayerType.GROUP_LAYER,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            flipH,
            flipV,

            parentId,
            title,
            materialIds,

            isAILayer,
            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
            children,
            data: {
                isExtraGroup: false,
                extension: {
                    role: GroupRoleType.PUZZLE,
                    edgeCornerRadius: edgeCornerRadius / 100,
                    edgeInnerWidth,
                    padding: edgeOuterWidth,
                    containerLayers: containerLayers.map((data: any, index) => {
                        if (data.stickerLayer) {
                            if (parseServerToClientMap[data.stickerLayer.layerType])
                                children.push(parseServerToClientMap[data.stickerLayer.layerType](data.stickerLayer));
                            else children.push(data.stickerLayer);
                            data.stickerLayer = index;
                        }
                        return {
                            left: data.left,
                            top: data.top,
                            width: data.width,
                            height: data.height,
                            stickerLayer: data.stickerLayer,
                            rotate: data.rotate,
                        };
                    }),
                    backgroundLayer,
                    puzzleMode,
                    frameScale,
                },
            },
        } as ILayerProps;
    },
};

export const formatImageTransformToClient = (
    imageTransform: IImageTransform,
    layerWidth: number,
    layerHeight: number,
) => {
    const { offsetX: offsetXRatio, offsetY: offsetYRatio, widthRatio, heightRatio, scale } = imageTransform;
    const offsetX = (offsetXRatio * layerWidth) / (widthRatio * scale * layerWidth);
    const offsetY = (offsetYRatio * layerHeight) / (heightRatio * scale * layerHeight);

    imageTransform.offsetX = offsetX;
    imageTransform.offsetY = offsetY;
    return cloneDeep(imageTransform);
};

/**
 * 将服务端获取的模板转化成客户端可用模板
 *
 * @param serverTemplate - 服务端获取的模板
 */
export const parseServerLayersToClient = (serverTemplate: ITemplateConfig, version: string): ILayerProps[] => {
    // const { templateConf, version } = serverTemplate;
    // const config = isArray(templateConf) ? templateConf[0] : templateConf;
    // const { layers, width, height } = config;
    const { width, height, layers } = serverTemplate;

    const newLayers = parseLayersToLatestVersion(layers, width, height, version as VERSION);

    const clientLayers = newLayers.map((layer) => {
        if (parseServerToClientMap[layer.layerType]) {
            return parseServerToClientMap[layer.layerType](layer);
        }
        return layer;
    }) as ILayerProps[];

    // 用于移动端兼容未知图层缩放的场景，在web端打开后该字段置为0
    traverseTrees(clientLayers, (layer) => {
        layer.fitHeight = 0;
        layer.fitWidth = 0;
    });

    return clientLayers;
};

// 强制声明导出 d.ts 文件
export type {};
